// src/hooks/useOrganization.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { organizationApi } from '../api/organizationApi';
import toast from 'react-hot-toast';

export const useOrganization = () => {
  const {
    organization,
    subscription,
    planLimits,
    usage: stats,
    loading: authLoading,
    refreshOrganization,
  } = useAuth();

  // Users e invitations caricati una sola volta
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const hasFetchedTeam = useRef(false);

  const loadTeamData = useCallback(async () => {
    if (hasFetchedTeam.current || !organization?.id) return;
    hasFetchedTeam.current = true;
    setTeamLoading(true);
    try {
      const [usersRes, invitesRes] = await Promise.all([
        organizationApi.getUsers ? organizationApi.getUsers() : Promise.resolve({ data: [] }),
        organizationApi.getInvitations()
      ]);
      setUsers(usersRes.data || []);
      setInvitations(invitesRes.data || []);
    } catch (err) {
      console.error('Errore caricamento team:', err);
    } finally {
      setTeamLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    if (organization?.id && !hasFetchedTeam.current) {
      loadTeamData();
    }
  }, [organization?.id, loadTeamData]);

  const refreshTeam = useCallback(async () => {
    hasFetchedTeam.current = false;
    await loadTeamData();
  }, [loadTeamData]);

  // ==================== AZIONI ====================

  const updateOrganization = async (data) => {
    if (!organization?.id) {
      toast.error('Organizzazione non trovata.');
      return { success: false };
    }
    try {
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '')
      );
      const response = await organizationApi.update(organization.id, cleanData);
      await refreshOrganization();
      toast.success('Organizzazione aggiornata!');
      return { success: true, data: response.data };
    } catch (err) {
      const message = err.response?.data?.message || 'Errore aggiornamento';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const inviteUser = async (email, role) => {
    try {
      const response = await organizationApi.inviteUser(email, role);
      setInvitations(prev => [response.data, ...prev]);
      toast.success(`Invito inviato a ${email}`);
      return { success: true, data: response.data };
    } catch (err) {
      const message = err.response?.data?.message || 'Errore invito';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const cancelInvitation = async (id) => {
    try {
      await organizationApi.cancelInvitation(id);
      setInvitations(prev => prev.filter(i => i.id !== id));
      toast.success('Invito cancellato');
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Errore cancellazione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendInvitation = async (id) => {
    try {
      await organizationApi.resendInvitation(id);
      toast.success('Invito rinviato');
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Errore invio';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const removeUser = async (userId) => {
    try {
      await organizationApi.removeUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utente rimosso');
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Errore rimozione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  return {
    organization,
    subscription,
    planLimits,
    stats,
    users,
    invitations,
    loading: authLoading || teamLoading,
    error: null,
    updateOrganization,
    inviteUser,
    cancelInvitation,
    resendInvitation,
    removeUser,
    refresh: refreshTeam,
  };
};